import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { LuMail } from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Avatar,
  Badge,
  Box,
  Card,
  Heading,
  HStack,
  Icon,
  Button,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { FilterBar, type FilterNode } from "../components/FilterBar";
import { FilterCount } from "../components/FilterCount";
import { searchUpdate } from "@/lib/search";
import { RoleIcon, resolveMemberRole } from "../components/RoleIcon";
import { groupLink } from "@/lib/groupLink";

type CeMMiesResult = FunctionReturnType<typeof api.persons.listCeMMies>;
type CeMMieRow = CeMMiesResult["persons"][number];

// The page means "the institute", so it opens on people with a counting
// affiliation. The rest of the roster is one choice away rather than mixed in:
// alumni still on a wrap-up agreement, and people whose start date is still
// ahead of them, are real records but not headcount.
export const CEMMIE_FILTERS = ["all", "active", "cemmie", "alumniActive", "alumni", "incoming"] as const;
type StatusFilter = (typeof CEMMIE_FILTERS)[number];

// Status is derived from affiliations, not from a flag: CeMMie, Alumni
// (active) for someone still on a collaboration agreement, Alumni, Incoming.
function DirectoryStatusBadge({ row }: { row: CeMMieRow }) {
  const palette =
    row.status === "cemmie"
      ? "green"
      : row.status === "alumniActive"
        ? "teal"
        : row.status === "incoming"
          ? "blue"
          : "gray";
  return (
    <Badge
      colorPalette={palette}
      variant={row.status === "cemmie" ? "solid" : "subtle"}
      textTransform="uppercase"
      letterSpacing="0.18em"
    >
      {row.statusLabel}
    </Badge>
  );
}

const columnHelper = createColumnHelper<CeMMieRow>();

// Defined once, outside the component. An inline arrow is a new
// function on every render, and a table told its row identity has
// changed cannot skip anything.
const getRowId = (row: { _id: string }) => row._id;

export function CeMMiesPage() {
  const result = useQuery(api.persons.listCeMMies, {});
  // The current CeMMies unless the address says otherwise (?status=alumni).
  const filter: StatusFilter = useSearch({ from: "/_authed/cemmies" }).status ?? "cemmie";
  const navigate = useNavigate({ from: "/cemmies" });
  const setFilter = (next: StatusFilter) =>
    void navigate({ search: { status: next === "cemmie" ? undefined : next }, ...searchUpdate });

  const persons = useMemo(() => result?.persons ?? [], [result]);
  // Active means holding a current affiliation, which is also what grants
  // access -- a CeMMie and an alumnus finishing a manuscript on a wrap-up
  // agreement are both here and both able to sign in. Alumni and incoming sit
  // beside it rather than under an "inactive" parent: nobody asks for "everyone
  // who is not here", and the parent only existed to hold two unrelated
  // answers.
  const STATUSES_IN: Record<StatusFilter, readonly string[] | null> = {
    all: null,
    active: ["cemmie", "alumniActive"],
    cemmie: ["cemmie"],
    alumniActive: ["alumniActive"],
    alumni: ["alumni"],
    incoming: ["incoming"],
  };

  const matches = (status: string, key: StatusFilter) => {
    const allowed = STATUSES_IN[key];
    return allowed === null || allowed.includes(status);
  };

  const data = useMemo<CeMMieRow[]>(
    () => persons.filter((person) => matches(person.status, filter)),
    [persons, filter],
  );

  const countOf = (key: StatusFilter) =>
    persons.filter((person) => matches(person.status, key)).length;

  // Everyone is the state before choosing anything, so it gets no chip.
  const filterOptions: FilterNode<StatusFilter>[] = [
    {
      key: "active",
      label: "Active",
      count: countOf("active"),
      description: "Currently at CeMM",
      children: [
        {
          key: "cemmie",
          label: "CeMMies",
          count: countOf("cemmie"),
          description: "At CeMM and counted in the headcount",
        },
        {
          key: "alumniActive",
          label: "Alumni (active)",
          count: countOf("alumniActive"),
          description:
            "Has left, but is still finishing work here, such as a manuscript or thesis. Not in the headcount.",
        },
      ],
    },
    {
      key: "alumni",
      label: "Alumni",
      count: countOf("alumni"),
      description: "Has left CeMM",
    },
    {
      key: "incoming",
      label: "Incoming",
      count: countOf("incoming"),
      description: "Starts at CeMM in the future",
    },
  ];

  const needsPrimary = result?.stats.needsPrimary ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.accessor("fullName", {
        header: "Name",
        cell: ({ row }) => {
          const person = row.original;
          return (
            <HStack gap={3}>
              <Avatar.Root size="md">
                <Avatar.Fallback name={person.fullName} />
                {person.profileImageUrl ? (
                  <Avatar.Image src={person.profileImageUrl} alt={`${person.fullName} profile`} />
                ) : null}
              </Avatar.Root>
              <Box>
                {person.shortName ? (
                  <Link to="/cemmies/$shortName" params={{ shortName: person.shortName }}>
                    <Text fontWeight="semibold" _hover={{ textDecoration: "underline" }}>{person.fullName}</Text>
                  </Link>
                ) : (
                  <Text fontWeight="semibold">{person.fullName}</Text>
                )}
                <HStack gap={2}>
                  <Text fontSize="xs" color="fg.muted">{person.shortName ?? "No user short name"}</Text>
                  {/* Elected representative bodies, from the Entra groups that
                      double as their mailing lists. Administration members in
                      those lists are excluded, since membership alone never
                      establishes the role. */}
                  {person.representativeRoles.map((role) => (
                    <Badge key={role} size="sm" colorPalette="purple" variant="subtle">
                      {role}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            </HStack>
          );
        },
      }),
      columnHelper.accessor("email", {
        header: "Email",
        cell: ({ row }) => (
          <HStack gap={2} fontSize="sm" color="fg.muted">
            <Icon as={LuMail} boxSize={4} />
            <Text>{row.original.email}</Text>
          </HStack>
        ),
      }),
      columnHelper.display({
        id: "job",
        header: "Position",
        cell: ({ row }) => {
          const affs = row.original.affiliations;
          const enrollment = row.original.enrollment;
          const isHead = affs.some((aff) => aff.headRole === "head");
          // The controlled position, not the raw job title.
          const titles = [...new Set(affs.map((aff) => aff.positionLabel).filter(Boolean))];

          if (enrollment) {
            const title = titles[0] ?? "Predoctoral Fellow";
            return (
              <Stack gap={1} fontSize="sm" color="fg.muted">
                <Text>{title}</Text>
                {enrollment.cohort === null ? null : (
                  <Text fontSize="xs">Cohort {enrollment.cohort}</Text>
                )}
              </Stack>
            );
          }

          const label = titles.length > 0 ? titles.join(" · ") : (isHead ? "Head" : null);
          if (!label) {
            return <Text fontSize="sm" color="fg.muted">—</Text>;
          }
          return <Text fontSize="sm" color="fg.muted">{label}</Text>;
        },
      }),
      columnHelper.display({
        id: "groups",
        header: "Groups",
        cell: ({ row }) => {
          const affs = row.original.affiliations;
          const isPhd = row.original.enrollment != null;
          if (affs.length === 0) {
            return <Text fontSize="sm" color="fg.muted">—</Text>;
          }
          return (
            <HStack
              gap={8}
              align="start"
              wrap="nowrap"
              minW={affs.length > 1 ? "380px" : "200px"}
              fontSize="sm"
              color="fg.muted"
            >
              {affs.map((aff, index) => {
                // Shared with the unit pages, so a person is the same symbol
                // wherever they appear.
                const role = resolveMemberRole({ headRole: aff.headRole, isStudent: isPhd });
                return (
                  <Stack key={aff.orgUnit?.slug ?? index} gap={1} flexShrink={0}>
                    <HStack gap={2}>
                      <RoleIcon role={role} />
                      {aff.orgUnit ? (
                        <Link to="/groups/$shortName" {...groupLink(aff.orgUnit)}>
                          <Text fontWeight="medium" color="fg" whiteSpace="nowrap" _hover={{ textDecoration: "underline" }}>{aff.orgUnit.name}</Text>
                        </Link>
                      ) : (
                        <Text whiteSpace="nowrap">—</Text>
                      )}
                    </HStack>
                    {aff.orgUnit ? (
                      <Text fontSize="xs">
                        {aff.orgUnit.shortName}
                        {aff.orgUnit.typeLabel ? ` · ${aff.orgUnit.typeLabel}` : ""}
                        {/* A non-counting basis: on site, but outside the headcount. */}
                        {aff.countsAsCeMMie ? "" : ` · ${aff.basisLabel}`}
                      </Text>
                    ) : null}
                  </Stack>
                );
              })}
            </HStack>
          );
        },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: ({ row }) => <DirectoryStatusBadge row={row.original} />,
      }),
    ],
    [],
  );

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="CeMMies"
        description="Everyone at CeMM, past and present, with their group and position."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "CeMMies" }]}
      />

      <Stack gap={8} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        <FilterBar
          label="Filter"
          options={filterOptions}
          value={filter}
          clearKey="all"
          onChange={setFilter}
        />

        {/* The count belongs with the table it describes, not with the
            filters above. */}
        <Stack gap={2} align="stretch">
          {result === undefined ? null : (
            <FilterCount
              shown={data.length}
              total={countOf("all")}
              noun={["person", "people"]}
              filtered={filter !== "all"}
              onClear={() => setFilter("all")}
            >
              {needsPrimary > 0 ? ` · ${needsPrimary} still need a primary group` : null}
            </FilterCount>
          )}
          {result === undefined ? null : data.length === 0 ? (
            <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
              <Card.Body gap={2} p={8} textAlign="center">
                <Heading size="lg">Nobody in this view</Heading>
                <Text fontSize="sm" color="fg.muted">
                  {result.persons.length === 0
                    ? "Nobody is in the directory yet."
                    : "No one matches the selected status. Try another filter."}
                </Text>
              </Card.Body>
            </Card.Root>
          ) : (
            <Box borderWidth="1px" borderColor="border">
              <DataTable
                data={data}
                columns={columns}
                getRowId={getRowId}
                enableSorting
                stickyHeader
                progressive
                initialBatchSize={20}
                progressiveBatchSize={500}
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
